import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../shared/api';
import {
  ErrorNote, Skeleton, formatDateTime, relativeTime, titleise, useToast,
} from '../shared/ui';
import { useAuth } from '../app/auth';
import {
  startRegistration,
  browserSupportsWebAuthn,
} from '../shared/webauthn-browser';

interface RoleDetail {
  code: string;
  label: string;
  description: string;
  approval_level: number | null;
  permission_count: number;
  is_privileged: boolean;
}

interface Profile {
  id: string;
  email: string;
  display_name: string;
  role: string;
  role_detail: RoleDetail;
  department: string | null;
  phone: string | null;
  mfa_enrolled: boolean;
  last_login_at: string | null;
  created_at: string;
  stats: {
    orders_created: number;
    approvals_given: number;
    rejections_given: number;
    value_approved: { minor: number; formatted: string };
  } | null;
}

interface PermissionItem {
  code: string;
  label: string;
  group: string;
  description?: string;
  sensitive?: boolean;
}

interface Activity {
  id: number;
  occurred_at: string;
  action: string;
  summary: string | null;
  ip: string | null;
}

type Tab = 'identity' | 'access' | 'security' | 'activity';

export function ProfilePage() {
  const [tab, setTab] = useState<Tab>('identity');
  const { data, isLoading, error } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.get<Profile>('/profile'),
  });

  if (isLoading) return <Skeleton rows={6} />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const initials = data.display_name
    .split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  const TABS: Array<[Tab, string]> = [
    ['identity', 'Identity'],
    ['access', 'What I can do'],
    ['security', 'Security'],
    ['activity', 'My activity'],
  ];

  return (
    <>
      <div className="page-head">
        <div className="row gap-14">
          <div className="avatar" style={{ width: 58, height: 58, fontSize: 20 }} aria-hidden="true">
            {initials}
          </div>
          <div>
            <div className="eyebrow">Your account</div>
            <h1 className="page-title" style={{ fontSize: 30 }}>{data.display_name}</h1>
            <div className="row gap-10 row-wrap mt-6">
              <span className={`pill ${data.role_detail.is_privileged ? 'pill-live' : 'pill-info'}`}>
                {data.role_detail.label}
              </span>
              {data.department && <span className="faint" style={{ fontSize: 13 }}>{data.department}</span>}
              <span className="ghost mono" style={{ fontSize: 12 }}>{data.email}</span>
            </div>
          </div>
        </div>
      </div>

      {data.stats && (
        <div className="grid grid-kpi" style={{ marginBottom: 18 }}>
          <Stat label="Orders raised" value={String(data.stats.orders_created)} />
          <Stat label="Approvals given" value={String(data.stats.approvals_given)} tone="var(--verify)" />
          <Stat label="Rejections" value={String(data.stats.rejections_given)} tone="var(--breach)" />
          <Stat label="Value approved" value={data.stats.value_approved.formatted} />
        </div>
      )}

      <div className="row gap-6 row-wrap" style={{ marginBottom: 16 }} role="tablist">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            className={`btn ${tab === key ? 'btn-primary' : 'btn-ghost'} btn-sm`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'identity' && <IdentityTab profile={data} />}
      {tab === 'access' && <AccessTab roleDetail={data.role_detail} />}
      {tab === 'security' && <SecurityTab profile={data} />}
      {tab === 'activity' && <ActivityTab />}
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: tone, fontSize: value.length > 10 ? 19 : undefined }}>
        {value}
      </div>
    </div>
  );
}

/* ── Identity ──────────────────────────────────────────────────────────── */

function IdentityTab({ profile }: { profile: Profile }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(profile.display_name);
  const [phone, setPhone] = useState(profile.phone ?? '');
  const [dept, setDept] = useState(profile.department ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.patch<Profile>('/profile', {
        display_name: name, phone: phone || null, department: dept || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['auth-me'] });
      toast('Profile updated', 'ok');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not save', 'bad'),
  });

  const dirty =
    name !== profile.display_name ||
    phone !== (profile.phone ?? '') ||
    dept !== (profile.department ?? '');

  return (
    <div className="split">
      <div className="card">
        <div className="card-head"><div className="card-title">Your details</div></div>
        <div className="col gap-14">
          <div className="field">
            <label className="label" htmlFor="pname">Display name</label>
            <input id="pname" className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label className="label" htmlFor="pphone">Phone</label>
            <input
              id="pphone" className="input" placeholder="+91…"
              value={phone} onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="pdept">Department</label>
            <input id="pdept" className="input" value={dept} onChange={(e) => setDept(e.target.value)} />
          </div>

          <div className="hint" style={{ borderStyle: 'solid' }}>
            Your email address and role are not editable here. Both are administrative
            changes with their own audit trail — ask an administrator.
          </div>

          <button
            className="btn btn-primary"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-head"><div className="card-title">Account</div></div>
        <div className="col gap-14">
          <Row label="Email" value={profile.email} mono />
          <Row label="Role" value={profile.role_detail.label} />
          <Row
            label="Approval level"
            value={profile.role_detail.approval_level ? `Level ${profile.role_detail.approval_level}` : 'Not an approver'}
          />
          <Row
            label="Last signed in"
            value={profile.last_login_at ? formatDateTime(profile.last_login_at) : 'This session'}
          />
          <Row label="Member since" value={formatDateTime(profile.created_at)} />
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className={mono ? 'mono' : ''} style={{ fontSize: 13.5, marginTop: 4 }}>{value}</div>
    </div>
  );
}

/* ── Access ────────────────────────────────────────────────────────────── */

function AccessTab({ roleDetail }: { roleDetail: RoleDetail }) {
  const { data, isLoading } = useQuery({
    queryKey: ['my-permissions'],
    queryFn: () =>
      api.get<{ granted: PermissionItem[]; denied: PermissionItem[] }>('/profile/permissions'),
  });

  if (isLoading) return <Skeleton rows={5} />;
  if (!data) return null;

  const groups = data.granted.reduce<Record<string, PermissionItem[]>>((acc, p) => {
    (acc[p.group] ??= []).push(p);
    return acc;
  }, {});

  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="row gap-14 row-wrap">
          <div className="grow">
            <div className="card-title" style={{ marginBottom: 6 }}>{roleDetail.label}</div>
            <div className="faint" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
              {roleDetail.description}
            </div>
          </div>
          <div className="right">
            <div className="mono" style={{ fontSize: 26, fontWeight: 700, color: 'var(--seal)' }}>
              {data.granted.length}
            </div>
            <div className="ghost" style={{ fontSize: 11 }}>
              of {data.granted.length + data.denied.length} permissions
            </div>
          </div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(310px,1fr))' }}>
        {Object.entries(groups).map(([group, items]) => (
          <div className="card" key={group}>
            <div className="card-head"><div className="card-title">{group}</div></div>
            <div className="col gap-10">
              {items.map((p) => (
                <div key={p.code}>
                  <div className="row gap-6">
                    <span style={{ color: 'var(--verify)' }}>✓</span>
                    <span style={{ fontSize: 13.5, fontWeight: 500 }}>{p.label}</span>
                    {p.sensitive && <span className="pill pill-wait">Sensitive</span>}
                  </div>
                  {p.description && (
                    <div className="faint" style={{ fontSize: 12, marginTop: 3, paddingLeft: 19, lineHeight: 1.5 }}>
                      {p.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {data.denied.length > 0 && (
          <div className="card">
            <div className="card-head">
              <div className="card-title ghost">Not available to your role</div>
            </div>
            <div className="col gap-6">
              {data.denied.map((p) => (
                <div key={p.code} className="row gap-6 ghost" style={{ fontSize: 12.5 }}>
                  <span>·</span> {p.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ── Security ──────────────────────────────────────────────────────────── */

function SecurityTab({ profile }: { profile: Profile }) {
  const toast = useToast();
  const { signOut } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const change = useMutation({
    mutationFn: () =>
      api.post('/profile/password', { current_password: current, new_password: next }),
    onSuccess: () => {
      toast('Password changed. Signing you back in…', 'ok');
      setTimeout(() => signOut(), 1200);
    },
    onError: (e) =>
      toast(e instanceof ApiError ? e.message : 'Could not change the password', 'bad'),
  });

  const checks = [
    { ok: next.length >= 12, label: 'At least 12 characters' },
    {
      ok: [/[a-z]/, /[A-Z]/, /\d/, /[^a-zA-Z0-9]/].filter((r) => r.test(next)).length >= 3,
      label: 'Three of: lowercase, uppercase, digits, symbols',
    },
    { ok: next.length > 0 && !next.toLowerCase().includes(profile.email.split('@')[0]!.toLowerCase()),
      label: 'Does not contain your email' },
    { ok: next.length > 0 && next === confirm, label: 'Both entries match' },
  ];
  const valid = current.length > 0 && checks.every((c) => c.ok);

  return (
    <div className="split">
      <div className="card">
        <div className="card-head"><div className="card-title">Change your password</div></div>
        <div className="col gap-14">
          <div className="field">
            <label className="label" htmlFor="cur">Current password</label>
            <input
              id="cur" className="input" type="password" autoComplete="current-password"
              value={current} onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="new">New password</label>
            <input
              id="new" className="input" type="password" autoComplete="new-password"
              value={next} onChange={(e) => setNext(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="conf">Confirm new password</label>
            <input
              id="conf" className="input" type="password" autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
            />
          </div>

          {next.length > 0 && (
            <div className="col gap-6">
              {checks.map((c) => (
                <div
                  key={c.label}
                  className="row gap-6"
                  style={{ fontSize: 12.5, color: c.ok ? 'var(--verify)' : 'var(--faint)' }}
                >
                  <span>{c.ok ? '✓' : '○'}</span> {c.label}
                </div>
              ))}
            </div>
          )}

          <div className="alert alert-warn">
            Changing your password signs out every other device immediately.
          </div>

          <button
            className="btn btn-primary"
            disabled={!valid || change.isPending}
            onClick={() => change.mutate()}
          >
            {change.isPending ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-head"><div className="card-title">Approval verification</div></div>
        <div className="faint" style={{ fontSize: 13, lineHeight: 1.65 }}>
          When you approve a payment order, a one-time code is sent to{' '}
          <span className="mono" style={{ color: 'var(--paper)' }}>{profile.email}</span>.
          The code is generated and checked on the server — it never passes through
          your browser, cannot be replayed, and expires after five minutes.
        </div>

        <hr className="hr" />

        <PasskeySection />
      </div>
    </div>
  );
}

/* ── Passkey management ───────────────────────────────────────────────── */

interface PasskeyItem {
  id: string;
  device_name: string;
  credential_id_b64: string;
  transports: string[] | null;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
}

function PasskeySection() {
  const toast = useToast();
  const qc = useQueryClient();
  const supported = browserSupportsWebAuthn();
  const [registering, setRegistering] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [showForm, setShowForm] = useState(false);

  const { data: passkeys, isLoading } = useQuery({
    queryKey: ['passkeys'],
    queryFn: () => api.get<PasskeyItem[]>('/auth/passkeys'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/auth/passkeys/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['passkeys'] });
      toast('Passkey removed', 'ok');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not remove passkey', 'bad'),
  });

  const doRegister = useCallback(async () => {
    if (registering) return;
    setRegistering(true);
    try {
      // 1. Get registration options from server
      const { options } = await api.post<{ options: Record<string, unknown> }>(
        '/auth/passkey/register/options',
        { device_name: deviceName || 'My passkey' },
      );

      // 2. Create credential via browser WebAuthn API
      const credential = await startRegistration(options);

      // 3. Send credential back to server for verification
      await api.post('/auth/passkey/register/verify', {
        credential,
        device_name: deviceName || 'My passkey',
      });

      qc.invalidateQueries({ queryKey: ['passkeys'] });
      toast('Passkey registered successfully', 'ok');
      setShowForm(false);
      setDeviceName('');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        toast('Registration cancelled', 'bad');
      } else {
        toast(err instanceof Error ? err.message : 'Could not register passkey', 'bad');
      }
    } finally {
      setRegistering(false);
    }
  }, [deviceName, registering, qc, toast]);

  return (
    <div>
      <div className="row between" style={{ marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 500 }}>Passkeys & biometrics</div>
          <div className="ghost" style={{ fontSize: 12, marginTop: 3 }}>
            Sign in with your fingerprint, face, or security key
          </div>
        </div>
        {supported && !showForm && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
            + Add passkey
          </button>
        )}
      </div>

      {!supported && (
        <div className="alert alert-warn" style={{ marginBottom: 14 }}>
          Your browser does not support passkeys. Try Chrome, Safari, or Edge on a device
          with biometric authentication.
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom: 14, background: 'var(--raised)' }}>
          <div className="col gap-14">
            <div className="field">
              <label className="label" htmlFor="pk-name">Device name</label>
              <input
                id="pk-name"
                className="input"
                placeholder="e.g. MacBook Touch ID, iPhone Face ID"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="row gap-10">
              <button
                className="btn btn-primary btn-sm"
                onClick={doRegister}
                disabled={registering}
              >
                {registering ? 'Waiting for authenticator…' : 'Register passkey'}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => { setShowForm(false); setDeviceName(''); }}
                disabled={registering}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading && <Skeleton rows={2} />}

      {passkeys && passkeys.length === 0 && !showForm && (
        <div className="faint" style={{ fontSize: 13, padding: '8px 0' }}>
          No passkeys registered yet. Add one to sign in with biometrics.
        </div>
      )}

      {passkeys && passkeys.length > 0 && (
        <div>
          {passkeys.map((pk) => (
            <div key={pk.id} className="passkey-item">
              <div className="passkey-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 10v4M7 15h10a2 2 0 002-2v-4a2 2 0 00-2-2H7a2 2 0 00-2 2v4a2 2 0 002 2z"/>
                  <circle cx="12" cy="12" r="1"/>
                </svg>
              </div>
              <div className="passkey-meta">
                <div className="passkey-name">{pk.device_name}</div>
                <div className="passkey-detail">
                  Added {relativeTime(pk.created_at)}
                  {pk.last_used_at && <> · Last used {relativeTime(pk.last_used_at)}</>}
                </div>
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  if (confirm(`Remove passkey "${pk.device_name}"?`)) {
                    remove.mutate(pk.id);
                  }
                }}
                disabled={remove.isPending}
                style={{ color: 'var(--breach)' }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Activity ──────────────────────────────────────────────────────────── */

function ActivityTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['my-activity'],
    queryFn: () => api.get<Activity[]>('/profile/activity?limit=100'),
  });

  if (isLoading) return <Skeleton rows={7} />;
  if (!data || data.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <div className="empty-title">No activity yet</div>
          <div>Everything you do in POAAS is recorded here and in the audit trail.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card card-flush">
      <div className="table-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>When</th>
              <th>What you did</th>
              <th>Action</th>
              <th>From</th>
            </tr>
          </thead>
          <tbody>
            {data.map((e) => (
              <tr key={e.id}>
                <td className="faint mono" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                  {relativeTime(e.occurred_at)}
                </td>
                <td style={{ fontSize: 13 }}>{e.summary ?? titleise(e.action)}</td>
                <td><span className="mono ghost" style={{ fontSize: 11 }}>{e.action}</span></td>
                <td className="mono ghost" style={{ fontSize: 11 }}>{e.ip ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
